import React from 'react';
import type {
    QuestionInsight,
    NumericQuestionInsight,
    BooleanQuestionInsight,
    CombinedNumeric,
    CombinedBoolean,
    CombinedText,
    TextQuestionInsight,
} from './insightsTypes';
import NumericCategoryOverview from './category/NumericCategoryOverview';
import BooleanCategoryOverview from './category/BooleanCategoryOverview';
import NumericQuestionCard from './question/NumericQuestionCard';
import BooleanQuestionCard from './question/BooleanQuestionCard';
import FreeformCategoryOverview from './category/FreeformCategoryOverview';
import FreeformQuestionCard from './question/FreeformQuestionCard';

/**
 * Lists every question type shown on the Insights tab and how to render it.
 *
 * The whole tab is built from this registry: the section headings, the combined
 * overviews and the question rows. That means adding a new question type, such as
 * multiple choice or formula, only touches these four places:
 *
 *   1. insightsTypes.ts: add the new `*QuestionInsight` shape to the QuestionInsight
 *      union, and a matching `Combined*` to CombinedInsights.
 *   2. Build two components: an <Overview> for the whole section, and a <Card> for one
 *      question. Colour any numbers with getScoreColor from insightsTheme.ts, and use
 *      NEUTRAL_COLOR / NEUTRAL_TRACK for anything that is not a value.
 *   3. Add one entry below, and add the type to INSIGHT_TYPE_ORDER.
 *   4. On the server, register a summariser in globalInsightsSummary and a combine
 *      helper in combineInsights.
 */

export type InsightType = QuestionInsight['type'];
export type AnyCombined = CombinedNumeric | CombinedBoolean | CombinedText;

export interface InsightTypeConfig {
    /** Heading shown above this section. */
    title: string;
    /** Renders the combined overview at the top of the section. */
    renderOverview: (questions: QuestionInsight[], combined: AnyCombined) => React.ReactNode;
    /** Renders the detail card shown when a question row is expanded. */
    renderCard: (insight: QuestionInsight) => React.ReactNode;
    /** Short figure shown on the collapsed row. Return '' to show nothing. */
    summaryMetric: (insight: QuestionInsight) => string;
    /**
     * One-line explanation of how summaryMetric is worked out, shown on hover over it.
     *
     * The row figure rolls every project together, so it is not the average of the
     * per-project bars underneath. Omit for types whose metric needs no explanation.
     */
    summaryMetricInfo?: string;
}

export const INSIGHT_REGISTRY: Record<InsightType, InsightTypeConfig> = {
    numeric: {
        title: 'Numeric insights',
        renderOverview: (questions, combined) => (
            <NumericCategoryOverview questions={questions as NumericQuestionInsight[]} combined={combined as CombinedNumeric} />
        ),
        renderCard: (insight) => <NumericQuestionCard insight={insight as NumericQuestionInsight} />,
        // Global numeric questions use a fixed 1–10 scale.
        summaryMetric: (insight) => (insight.type === 'numeric' ? `${insight.overall.average.toFixed(1)} / 10` : ''),
        summaryMetricInfo: 'The average of every answer to this question, so projects with more answers count for more.',
    },
    boolean: {
        title: 'Yes / No insights',
        renderOverview: (questions, combined) => (
            <BooleanCategoryOverview questions={questions as BooleanQuestionInsight[]} combined={combined as CombinedBoolean} />
        ),
        renderCard: (insight) => <BooleanQuestionCard insight={insight as BooleanQuestionInsight} />,
        summaryMetric: (insight) => (insight.type === 'boolean' ? `${Math.round(insight.overall.yesPct)}% Yes` : ''),
        summaryMetricInfo: 'The share of every answer to this question that was yes, so projects with more answers count for more.',
    },
    text: {
        title: 'Free-form text insights',
        renderOverview: (_questions, combined) => <FreeformCategoryOverview combined={combined as CombinedText} />,
        renderCard: (insight) => <FreeformQuestionCard insight={insight as TextQuestionInsight} />,
        // Free-form has no single metric; the row just shows the response count.
        summaryMetric: () => '',
    },
};

/** Order the sections appear in on the Insights tab. */
export const INSIGHT_TYPE_ORDER: InsightType[] = ['numeric', 'boolean', 'text'];
